import { Box, Stack, Text } from "@sembark-travel/ui-base"
import type { TWhatsAppMessage } from "../store"

// How tall a quoted message may get before it scrolls. Roughly four lines --
// enough to read the gist of a template without the quote dwarfing the reply.
const QUOTE_MAX_HEIGHT = "5rem"

/**
 * The message a reply answers, quoted the way WhatsApp shows a reply: above
 * the reply's bubble, and above the reply box while one is being written.
 * Media-only originals have no text to quote, so they are described instead
 * of rendering an empty line.
 */
export function WhatsAppQuotedMessage({
	quoted,
}: {
	quoted: NonNullable<TWhatsAppMessage["context_message"]>
}) {
	const body = quoted.message?.trim()

	return (
		<Box
			paddingX="2"
			paddingY="1"
			rounded="md"
			bgColor="subtle"
			borderLeftWidth="4"
			borderColor="accent"
		>
			<Stack gap="px">
				<Text fontSize="xs" color="muted">
					{quoted.direction === "inbound" ? "Customer" : "You"}
				</Text>
				{quoted.rendered_html || body ? (
					// WhatsApp crops a quote to two lines; a quoted template can
					// be much longer than that, so it scrolls instead of hiding
					// the part that explains what was answered.
					<Box
						fontSize="sm"
						color="muted"
						overflow="auto"
						style={{ maxHeight: QUOTE_MAX_HEIGHT }}
					>
						{quoted.rendered_html ? (
							// Rendered server-side, the same way the bubble renders
							// its own body.
							<Box dangerouslySetInnerHTML={{ __html: quoted.rendered_html }} />
						) : (
							<Text whiteSpace="preserveLine">{body}</Text>
						)}
					</Box>
				) : (
					<Text fontSize="sm" color="muted">
						{quoted.media_type ? `[${quoted.media_type}]` : "Original message"}
					</Text>
				)}
			</Stack>
		</Box>
	)
}
